import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, CornerDownRight } from "lucide-react";
import type {
  BuyerInterviewQuestionDto,
  BuyerInterviewRegistryDto,
  BuyerInterviewSectionProgressDto,
} from "@/api/buyers";
import { ApiError, extractErrorMessage } from "@/api/client";
import {
  AutosaveIndicator,
  Button,
  Input,
  Skeleton,
  Textarea,
  useToast,
  type AutosaveState,
} from "@/design-system/components/ui";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { cn } from "@/design-system/lib/utils";
import { InlineQueryError } from "../queryStates";
import {
  CallExperienceFields,
  CallFinancialFields,
  CallMotivationFields,
  CallPrequalificationSection,
} from "./CallPrequalFields";
import { InterviewTranscript } from "./InterviewTranscript";
import { PrequalCallReview } from "./PrequalCallReview";
import {
  BLANK_ANSWER,
  effectiveLiveAnswers,
  isAskedLive,
  isRecorded,
  liveOptions,
  questionsForSection,
  toDraftAnswers,
  type DraftAnswer,
  type DraftAnswers,
} from "./interviewBits";
import {
  useCallAnswersAutosave,
  usePrequalifyCall,
  type CallAnswersAutosave,
} from "./usePrequalCall";
import {
  recordedWordingVersion,
  useBuyerInterview,
  useCompleteInterview,
  useInterviewRegistry,
  useSaveInterviewAnswer,
} from "./useBuyerInterview";

/**
 * The guided qualification interview — a phone call, recorded.
 *
 * <p>🔴 <b>NOTHING IN THIS DRAWER RATES THE BUYER.</b> No verdict, no score, no
 * tone on an answer, no knockout warning, no "you are close to a pass". The
 * server omits the outcome entirely from an in-progress interview, so there is
 * nothing here to leak — and this component must not invent one, because a
 * rating hint on screen mid-call steers what the specialist writes down, which
 * is the one failure the whole design exists to prevent. Coverage counts ("3 of
 * 3 recorded") are the deliberate exception: they say how much was asked, never
 * how it went.</p>
 *
 * <p><b>Free navigation, because a real call does not follow your order.</b>
 * The rail jumps to any section at any time; Back/Next are a convenience, not a
 * wizard's rails.</p>
 *
 * <p><b>Every answer autosaves on its own.</b> Choices save the instant they are
 * clicked; text debounces so a sentence is not fifty writes. A dropped call
 * loses at most the last {@link TEXT_DEBOUNCE_MS} milliseconds of typing, and
 * closing the drawer flushes whatever is still pending.</p>
 *
 * <p><b>One call (owner brief, 2026-10-16).</b> The interview IS the call, and
 * its prequalification half is asked inside it: each section carries the
 * funnel's own fields for the questions the call derives (`CallPrequalFields`),
 * a retired question is never asked, a derived one is not asked, a partly
 * derived one asks only its listed choices, and TL1 is not asked once the call
 * has derived it. The Prequalification section is optional (D8) — the
 * interview completes without it, and its answers persist to be finished
 * later, here, on a completed interview too. Prequalify generates documents
 * and sends nothing; the review (`PrequalCallReview`) opens on it.</p>
 *
 * <p><b>Wording (admin-editable, versioned).</b> An interview in progress shows
 * the CURRENT wording — what completion will record it with. When a version
 * was published after this interview started, the server says so
 * (`wordingChangedSinceStart`): the drawer refetches the current wording once
 * and tells the specialist, in one line. A completed interview's transcript
 * renders in the wording it was recorded with (`wordingVersion`).</p>
 */

/** Shown on an interview in progress when new wording was published after it
 *  started. True of what the drawer does: it refetches and shows the current
 *  wording, and completion stamps that version. */
export const WORDING_CHANGED_NOTICE =
  "The interview's wording was updated after this interview started. It shows the wording in force now; completing records the wording in force at that moment.";

/** Long enough that a typed sentence is one write, short enough that hanging up
 *  mid-sentence loses nothing worth having. */
const TEXT_DEBOUNCE_MS = 600;

const REVIEW = "__review__";
const PREQUAL = "__prequal__";

export function BuyerInterviewDrawer({
  buyerLeadId,
  open,
  onClose,
}: {
  buyerLeadId: string;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  // The CURRENT wording: what an interview in progress is asked and recorded in.
  const registryQuery = useInterviewRegistry(undefined, open);
  const interviewQuery = useBuyerInterview(buyerLeadId, open);
  const registry = registryQuery.data;
  const interview = interviewQuery.data ?? null;
  const completedInterview = interview?.status === "Completed" ? interview : null;
  // A completed interview's transcript reads in the wording it was recorded with.
  const recordedRegistryQuery = useInterviewRegistry(
    completedInterview ? recordedWordingVersion(completedInterview) : 0,
    open && completedInterview != null,
  );
  const inProgress = open && interview?.status === "InProgress";
  const wordingChanged = inProgress && interview?.wordingChangedSinceStart === true;

  // New wording was published after this interview started: the cached current
  // wording may predate it, so read it again. When the server names the version
  // in force (`currentWordingVersion`), refetch whenever the wording shown is a
  // different one — so a second publish while the drawer stays open is picked
  // up too — at most once per version, so a server answering with a fallback
  // version cannot loop. Without it, once per interview (each autosave response
  // carries the flag again).
  const currentWording = inProgress ? (interview?.currentWordingVersion ?? null) : null;
  const shownWording = registry?.wordingVersion ?? null;
  const refetchedFor = useRef<string | null>(null);
  const refetchRegistry = registryQuery.refetch;
  const interviewId = interview?.id ?? null;
  useEffect(() => {
    if (interviewId == null) return;
    let key: string | null = null;
    if (currentWording != null) {
      if (shownWording != null && shownWording !== currentWording) key = `v${currentWording}`;
    } else if (wordingChanged) {
      key = `i${interviewId}`;
    }
    if (key == null || refetchedFor.current === key) return;
    refetchedFor.current = key;
    void refetchRegistry();
  }, [currentWording, shownWording, wordingChanged, interviewId, refetchRegistry]);
  const call = useCallAnswersAutosave(buyerLeadId, open);

  const [answers, setAnswers] = useState<DraftAnswers>({});
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>("");
  const [saveState, setSaveState] = useState<AutosaveState>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [failed, setFailed] = useState<string[]>([]);

  // Seeded DURING RENDER rather than in an effect, the React-sanctioned shape
  // for "reset state when the thing being edited changes". An effect keyed on
  // the interview OBJECT would re-run on every autosave response — the response
  // is a fresh object — and clobber whatever the specialist had typed since.
  // Local answers are the input; the server's `progress` is the output. They
  // deliberately never flow back the other way.
  if (open && interview && seededFor !== interview.id) {
    setSeededFor(interview.id);
    setAnswers(toDraftAnswers(interview.answers));
    setActiveSection(registry?.sections[0]?.id ?? "");
  }
  if (!open && seededFor !== null) {
    setSeededFor(null);
  }
  // The registry and the interview resolve independently, so seeding may have
  // run before there were any sections to point at.
  if (open && registry && activeSection === "") {
    setActiveSection(registry.sections[0]?.id ?? "");
  }

  // ── autosave ────────────────────────────────────────────────────────────
  const seqRef = useRef(0);
  const latestSeqRef = useRef(0);
  const inflightRef = useRef(0);
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const pendingRef = useRef(new Map<string, DraftAnswer>());

  // 🔴 Read the current answers from a MIRROR, never from inside a `setAnswers`
  // updater. React may invoke an updater twice (StrictMode does, in
  // development), and an updater that fires a write would then send the same
  // answer twice — two PUTs racing, on the surface whose whole job is to record
  // exactly what was said.
  const answersRef = useRef<DraftAnswers>({});
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // Mirrored for the same reason: a save settling has to know, right then,
  // whether any OTHER question is still unsaved — or the indicator flips to
  // "Saved ✓" beside a Retry button that is still offering to save something.
  const failedRef = useRef<string[]>([]);
  const markFailed = useCallback((questionId: string) => {
    if (failedRef.current.includes(questionId)) return;
    failedRef.current = [...failedRef.current, questionId];
    setFailed(failedRef.current);
  }, []);
  const clearFailed = useCallback((questionId: string) => {
    if (!failedRef.current.includes(questionId)) return;
    failedRef.current = failedRef.current.filter((id) => id !== questionId);
    setFailed(failedRef.current);
  }, []);

  const applyIfLatest = useCallback((seq: number) => seq === latestSeqRef.current, []);
  const save = useSaveInterviewAnswer(buyerLeadId, applyIfLatest);
  const saveMutate = save.mutate;

  const fire = useCallback(
    (questionId: string, answer: DraftAnswer) => {
      // 🔴 Load-bearing. A question can carry BOTH choices and a text box, so a
      // click can land while that question's typing is still on a debounce.
      // Dropping the pending entry here is what stops the timer firing a second
      // write behind this one with the same answer.
      pendingRef.current.delete(questionId);
      const seq = ++seqRef.current;
      latestSeqRef.current = seq;
      inflightRef.current += 1;
      setSaveState("saving");

      saveMutate(
        {
          seq,
          questionId,
          choices: answer.choices,
          // A blank box CLEARS the text. There is no separate flag: an
          // interview answer is exactly what was written down, and a blank IS
          // "nothing recorded".
          text: answer.text.trim().length > 0 ? answer.text : null,
        },
        {
          onSuccess: () => {
            inflightRef.current -= 1;
            clearFailed(questionId);
            setLastSavedAt(new Date().toISOString());
            if (inflightRef.current === 0) {
              setSaveState(failedRef.current.length > 0 ? "error" : "saved");
            }
          },
          onError: (error: unknown) => {
            inflightRef.current -= 1;
            // Kept pending, so Retry re-sends exactly what failed rather than
            // whatever the field happens to hold by then.
            pendingRef.current.set(questionId, answer);
            markFailed(questionId);
            setSaveState("error");
            toast.error(extractErrorMessage(error));
          },
        },
      );
    },
    [clearFailed, markFailed, saveMutate, toast],
  );

  /** What a write may record for a question: on a `partlyDerived` one only the
   *  choices the call asks — the rest are the derivation's, and a seeded copy of
   *  them must never be written back as though the specialist had said it. */
  const ownChoices = useCallback(
    (questionId: string, choices: string[]) => {
      const question = registry?.questions.find((q) => q.id === questionId);
      if (question?.callSource !== "partlyDerived") return choices;
      const asked = question.callAskedOptionKeys ?? [];
      return choices.filter((k) => asked.includes(k));
    },
    [registry],
  );

  /** Choices save immediately — a click is a decision, and the coverage count
   *  it moves should follow it without a pause. */
  const commitChoices = useCallback(
    (questionId: string, choices: string[]) => {
      const next: DraftAnswer = {
        ...(answersRef.current[questionId] ?? BLANK_ANSWER),
        choices: ownChoices(questionId, choices),
      };
      answersRef.current = { ...answersRef.current, [questionId]: next };
      setAnswers((current) => ({ ...current, [questionId]: next }));
      fire(questionId, next);
    },
    [fire, ownChoices],
  );

  /** Text debounces. The local value updates on every keystroke so the field is
   *  never laggy; only the write waits. */
  const commitText = useCallback(
    (questionId: string, text: string) => {
      const current = answersRef.current[questionId] ?? BLANK_ANSWER;
      const next: DraftAnswer = {
        choices: ownChoices(questionId, current.choices),
        text,
      };
      answersRef.current = { ...answersRef.current, [questionId]: next };
      setAnswers((current) => ({ ...current, [questionId]: next }));

      pendingRef.current.set(questionId, next);
      const timers = timersRef.current;
      const existing = timers.get(questionId);
      if (existing) clearTimeout(existing);
      timers.set(
        questionId,
        setTimeout(() => {
          timers.delete(questionId);
          const queued = pendingRef.current.get(questionId);
          if (queued) fire(questionId, queued);
        }, TEXT_DEBOUNCE_MS),
      );
    },
    [fire, ownChoices],
  );

  /** Write everything still waiting on a debounce. Called before the drawer
   *  closes, so hanging up never costs the last sentence. */
  const flushPending = useCallback(() => {
    const timers = timersRef.current;
    for (const [questionId, timer] of timers) {
      clearTimeout(timer);
      const queued = pendingRef.current.get(questionId);
      if (queued) fire(questionId, queued);
    }
    timers.clear();
  }, [fire]);

  // Timers outlive a close if nothing clears them, and firing a write from an
  // unmounted drawer is a write nobody can see fail.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const callFlush = call.flush;
  const handleClose = useCallback(() => {
    flushPending();
    // A failed save is already on the indicator and stays pending; closing
    // does not wait on it.
    void callFlush().catch(() => undefined);
    onClose();
  }, [callFlush, flushPending, onClose]);

  const callRetry = call.retry;
  const callFailed = call.failed;
  const retryFailed = useCallback(() => {
    for (const questionId of failedRef.current) {
      const answer = pendingRef.current.get(questionId) ?? answersRef.current[questionId];
      if (answer) fire(questionId, answer);
    }
    if (callFailed) callRetry();
  }, [callFailed, callRetry, fire]);

  // ── the call's prequalification ─────────────────────────────────────────
  const prequalify = usePrequalifyCall(buyerLeadId);
  const [confirmingDuplicate, setConfirmingDuplicate] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<string | null>(null);

  /** Save what is on screen, then prequalify it. A 409 is a live invitation the
   *  server would duplicate: say so, and retry only on the specialist's word. */
  const runPrequalify = async (confirmDuplicate: boolean) => {
    try {
      await callFlush();
    } catch (error) {
      toast.error(extractErrorMessage(error));
      return;
    }
    prequalify.mutate(confirmDuplicate ? { confirmDuplicate: true } : {}, {
      onSuccess: (result) => {
        setConfirmingDuplicate(null);
        setReviewing(result.submissionId);
      },
      onError: (error: unknown) => {
        if (error instanceof ApiError && error.status === 409 && !confirmDuplicate) {
          setConfirmingDuplicate(extractErrorMessage(error));
        } else {
          setConfirmingDuplicate(null);
          toast.error(extractErrorMessage(error));
        }
      },
    });
  };

  // ── completion ──────────────────────────────────────────────────────────
  const complete = useCompleteInterview(buyerLeadId);
  const missingCount = interview?.missingSpineQuestionIds.length ?? 0;
  const spineComplete = interview?.spineComplete ?? false;
  const alreadyCompleted = interview?.status === "Completed";

  const progressBySection = useMemo(() => {
    const map = new Map<string, BuyerInterviewSectionProgressDto>();
    for (const p of interview?.progress ?? []) map.set(p.sectionId, p);
    return map;
  }, [interview?.progress]);

  const sections = registry?.sections ?? [];
  const activeIndex = sections.findIndex((s) => s.id === activeSection);
  const onReview = activeSection === REVIEW;
  const onPrequal = activeSection === PREQUAL;

  const goNext = () => {
    if (activeIndex >= 0 && activeIndex < sections.length - 1) {
      setActiveSection(sections[activeIndex + 1].id);
    } else if (onPrequal) {
      setActiveSection(REVIEW);
    } else {
      setActiveSection(PREQUAL);
    }
  };
  const goBack = () => {
    if (onReview) {
      setActiveSection(PREQUAL);
    } else if (onPrequal) {
      setActiveSection(sections[sections.length - 1]?.id ?? "");
    } else if (activeIndex > 0) {
      setActiveSection(sections[activeIndex - 1].id);
    }
  };

  const derivedIds = useMemo(
    () => new Set(interview?.derivedQuestionIds ?? []),
    [interview?.derivedQuestionIds],
  );
  // What the questions and the review read: local input, with every answer the
  // call derives taken from the server.
  const effective = useMemo(
    () => effectiveLiveAnswers(registry, answers, toDraftAnswers(interview?.answers), derivedIds),
    [registry, answers, interview?.answers, derivedIds],
  );

  // One indicator for both writers on this screen.
  const indicatorState: AutosaveState =
    saveState === "error" || call.saveState === "error"
      ? "error"
      : saveState === "saving" || call.saveState === "saving"
        ? "saving"
        : saveState === "saved" || call.saveState === "saved"
          ? "saved"
          : "idle";
  const indicatorSavedAt =
    [lastSavedAt, call.lastSavedAt].filter((t): t is string => t != null).sort().pop() ?? null;

  const submitComplete = async () => {
    flushPending();
    // The call's answers are what completion stamps the derived answers from,
    // so they must be on the server first.
    try {
      await callFlush();
    } catch (error) {
      toast.error(extractErrorMessage(error));
      return;
    }
    complete.mutate(undefined, {
      onSuccess: () => {
        toast.success("Interview completed.");
        onClose();
      },
      onError: (error: unknown) => toast.error(extractErrorMessage(error)),
    });
  };

  const loading = registryQuery.isLoading || interviewQuery.isLoading;
  const failedToLoad = registryQuery.isError || interviewQuery.isError;

  return (
    <>
    <ResourceDrawer
      open={open}
      onClose={handleClose}
      title="Qualification interview"
      subtitle="Record what they said. Phoenix works out what it means when you finish."
      width="xl"
      testId="buyer-interview-drawer"
      footer={
        <div className="flex flex-1 flex-wrap items-center justify-between gap-space-2">
          <div className="flex items-center gap-space-3">
            <AutosaveIndicator
              state={indicatorState}
              lastSavedAt={indicatorSavedAt}
              idleHint="Every answer saves as you record it."
              data-testid="interview-autosave"
            />
            {(failed.length > 0 || call.failed) && (
              <Button size="sm" variant="secondary" onClick={retryFailed}>
                Retry saving
              </Button>
            )}
          </div>
          <div className="flex items-center gap-space-2">
            {/* NOT "Close": `ResourceDrawer`'s header X carries
                `aria-label="Close"`, so two controls in one dialog would share
                an accessible name — an assistive-technology problem before it
                is a test one. The name also states what the button does, which
                is flush the pending write and then leave. */}
            <Button variant="text" onClick={handleClose}>
              Save and close
            </Button>
            <Button
              variant="secondary"
              onClick={goBack}
              disabled={!onReview && activeIndex <= 0}
            >
              Back
            </Button>
            {onReview ? (
              <Button
                onClick={() => void submitComplete()}
                loading={complete.isPending}
                disabled={!spineComplete || complete.isPending || alreadyCompleted}
                data-testid="interview-complete"
              >
                Complete interview
              </Button>
            ) : (
              <Button onClick={goNext} data-testid="interview-next">
                Next
              </Button>
            )}
          </div>
        </div>
      }
    >
      {loading && <Skeleton className="h-64 w-full" />}

      {!loading && failedToLoad && (
        <InlineQueryError
          what="the interview"
          onRetry={() => {
            void registryQuery.refetch();
            void interviewQuery.refetch();
          }}
          retrying={registryQuery.isFetching || interviewQuery.isFetching}
        />
      )}

      {/* Defensive: the panel starts an interview before it opens this drawer,
          and the start response is written straight into the cache — so an open
          drawer with no interview means the start was lost between the two. Say
          so rather than rendering an empty box. */}
      {!loading && !failedToLoad && !interview && (
        <p className="text-small text-gray-mid m-0" role="status">
          No interview is open. Close this and start one from the Qualification
          tab.
        </p>
      )}

      {!loading && !failedToLoad && registry && interview && alreadyCompleted && (
        <div>
          {/* Reached when a completion lands while the drawer is open, and from
              the panel's "Finish prequalification" (D8). The interview's
              answers are refused server-side either way — a completed
              interview is immutable, and re-interviewing mints a new version —
              but the call's prequalification can still be finished here. */}
          <p className="text-small text-gray-mid mt-0 mb-space-4" role="status">
            This interview is complete. Its answers are fixed — re-interviewing
            starts a new version rather than editing this one. The
            prequalification below can still be finished.
          </p>
          <div className="grid gap-space-4">
            <CallFields call={call} sectionId="FC" />
            <CallFields call={call} sectionId="EXP" />
            <CallFields call={call} sectionId="MF" />
            <PrequalBody
              call={call}
              buyerLeadId={buyerLeadId}
              onPrequalify={() => void runPrequalify(false)}
              prequalifying={prequalify.isPending}
              onReview={setReviewing}
            />
            {recordedRegistryQuery.isError ? (
              <InlineQueryError
                what="the interview's wording"
                onRetry={() => void recordedRegistryQuery.refetch()}
                retrying={recordedRegistryQuery.isFetching}
              />
            ) : recordedRegistryQuery.data ? (
              <InterviewTranscript registry={recordedRegistryQuery.data} answers={effective} />
            ) : (
              <Skeleton className="h-32 w-full" />
            )}
          </div>
        </div>
      )}

      {!loading && !failedToLoad && registry && interview && wordingChanged && (
        <p
          className="text-small text-gray-mid mt-0 mb-space-4"
          role="status"
          data-testid="interview-wording-changed"
        >
          {WORDING_CHANGED_NOTICE}
        </p>
      )}

      {!loading && !failedToLoad && registry && interview && !alreadyCompleted && (
        // Rail-plus-body, driven by the CONTAINER and not the viewport: this
        // drawer is a fixed-max-width overlay, so a `md:` breakpoint asks the
        // window a question only the drawer can answer (Table & Tile Standard
        // §12). The mechanism is `ResponsiveSplit`'s, documented at length on
        // that component — a lopsided `grow` weighting, so the rail holds its
        // basis beside the body and goes full width the moment it wraps. It is
        // inlined rather than reused because the rail comes FIRST here: a
        // section nav that a keyboard user reaches only after forty questions
        // is not a nav.
        <div className="flex min-w-0 flex-wrap items-start gap-space-4">
          <div className="grow shrink-0" style={{ flexBasis: "14rem" }}>
            <SectionRail
              registry={registry}
              progress={progressBySection}
              active={activeSection}
              onSelect={setActiveSection}
            />
          </div>

          <div className="min-w-0 grow-[999]" style={{ flexBasis: "22rem" }}>
            {onReview ? (
              <ReviewStep
                registry={registry}
                answers={effective}
                recordedCount={Object.values(answers).filter(isRecorded).length}
                missingCount={missingCount}
                spineComplete={spineComplete}
              />
            ) : onPrequal ? (
              <div>
                <h3 className="font-display font-bold text-h3 text-onyx m-0">Prequalification</h3>
                <p className="text-small text-gray-mid mt-space-1 mb-space-4">
                  Optional. The interview completes without it, and what you record
                  here is kept to finish later.
                </p>
                <PrequalBody
                  call={call}
                  buyerLeadId={buyerLeadId}
                  onPrequalify={() => void runPrequalify(false)}
                  prequalifying={prequalify.isPending}
                  onReview={setReviewing}
                />
              </div>
            ) : (
              <SectionBody
                registry={registry}
                sectionId={activeSection}
                answers={answers}
                effective={effective}
                derivedIds={derivedIds}
                call={call}
                onChoices={commitChoices}
                onText={commitText}
              />
            )}
          </div>
        </div>
      )}
    </ResourceDrawer>
    <ConfirmModal
      open={confirmingDuplicate !== null}
      title="Prequalify anyway?"
      message={
        <>
          <p className="m-0">{confirmingDuplicate}</p>
          <p className="mt-space-2 mb-0">
            This buyer already has a live prequalification invitation. Prequalifying on this
            call closes it.
          </p>
        </>
      }
      confirmLabel="Prequalify on this call"
      onConfirm={() => void runPrequalify(true)}
      onCancel={() => setConfirmingDuplicate(null)}
    />
    {reviewing && (
      <PrequalCallReview
        buyerId={buyerLeadId}
        submissionId={reviewing}
        open
        onClose={() => setReviewing(null)}
      />
    )}
    </>
  );
}

/** The call's own fields for one interview section, or nothing for a section
 *  the call asks nothing extra in. A failed state read says so in place. */
function CallFields({ call, sectionId }: { call: CallAnswersAutosave; sectionId: string }) {
  if (sectionId !== "FC" && sectionId !== "EXP" && sectionId !== "MF") return null;
  if (call.stateQuery.isError) {
    return (
      <InlineQueryError
        what="the call's prequalification answers"
        onRetry={() => void call.stateQuery.refetch()}
        retrying={call.stateQuery.isFetching}
      />
    );
  }
  if (!call.answers) return null;
  const props = { answers: call.answers, onChange: call.update, funnelCopy: call.state?.funnelCopy };
  if (sectionId === "FC") return <CallFinancialFields {...props} />;
  if (sectionId === "EXP") return <CallExperienceFields {...props} />;
  return <CallMotivationFields {...props} />;
}

function PrequalBody({
  call,
  buyerLeadId,
  onPrequalify,
  prequalifying,
  onReview,
}: {
  call: CallAnswersAutosave;
  buyerLeadId: string;
  onPrequalify: () => void;
  prequalifying: boolean;
  onReview: (submissionId: string) => void;
}) {
  if (call.stateQuery.isError) {
    return (
      <InlineQueryError
        what="the call's prequalification answers"
        onRetry={() => void call.stateQuery.refetch()}
        retrying={call.stateQuery.isFetching}
      />
    );
  }
  if (!call.answers) return <Skeleton className="h-32 w-full" />;
  return (
    <CallPrequalificationSection
      buyerLeadId={buyerLeadId}
      answers={call.answers}
      onChange={call.update}
      state={call.state}
      onPrequalify={onPrequalify}
      prequalifying={prequalifying}
      onReview={onReview}
    />
  );
}

/**
 * The section rail. Each row carries its COVERAGE — how many of that section's
 * required questions have something recorded against them — and nothing else.
 */
function SectionRail({
  registry,
  progress,
  active,
  onSelect,
}: {
  registry: BuyerInterviewRegistryDto;
  progress: Map<string, BuyerInterviewSectionProgressDto>;
  active: string;
  onSelect: (id: string) => void;
}) {
  return (
    <nav aria-label="Interview sections" className="grid gap-space-1 content-start">
      {registry.sections.map((section) => {
        const p = progress.get(section.id);
        const answered = p?.spineAnswered ?? 0;
        const total = p?.spineTotal ?? 0;
        const covered = total > 0 && answered >= total;
        return (
          <button
            key={section.id}
            type="button"
            onClick={() => onSelect(section.id)}
            aria-current={active === section.id ? "true" : undefined}
            className={cn(
              "w-full text-left rounded-md px-space-3 py-space-2 border",
              "transition-colors duration-fast ease-standard",
              active === section.id
                ? "border-gold bg-white-warm"
                : "border-border-light hover:bg-white-warm",
            )}
          >
            <span className="flex items-center gap-space-2 text-small text-onyx">
              {covered && (
                <CheckCircle2 size={13} className="text-status-success shrink-0" aria-hidden />
              )}
              {section.title}
            </span>
            <span className="block text-caption text-gray-mid">
              {answered} of {total} recorded
            </span>
          </button>
        );
      })}
      <button
        type="button"
        onClick={() => onSelect(PREQUAL)}
        aria-current={active === PREQUAL ? "true" : undefined}
        data-testid="interview-rail-prequal"
        className={cn(
          "w-full text-left rounded-md px-space-3 py-space-2 border mt-space-2",
          "transition-colors duration-fast ease-standard",
          active === PREQUAL
            ? "border-gold bg-white-warm"
            : "border-border-light hover:bg-white-warm",
        )}
      >
        <span className="block text-small text-onyx">Prequalification</span>
        <span className="block text-caption text-gray-mid">Optional</span>
      </button>
      <button
        type="button"
        onClick={() => onSelect(REVIEW)}
        aria-current={active === REVIEW ? "true" : undefined}
        className={cn(
          "w-full text-left rounded-md px-space-3 py-space-2 border mt-space-2",
          "transition-colors duration-fast ease-standard",
          active === REVIEW
            ? "border-gold bg-white-warm"
            : "border-border-light hover:bg-white-warm",
        )}
      >
        <span className="block text-small text-onyx">Review &amp; finish</span>
      </button>
    </nav>
  );
}

function SectionBody({
  registry,
  sectionId,
  answers,
  effective,
  derivedIds,
  call,
  onChoices,
  onText,
}: {
  registry: BuyerInterviewRegistryDto;
  sectionId: string;
  /** What the specialist recorded — what each card shows and edits. */
  answers: DraftAnswers;
  /** Recorded plus derived — what decides which branches are open. */
  effective: DraftAnswers;
  derivedIds: ReadonlySet<string>;
  call: CallAnswersAutosave;
  onChoices: (questionId: string, choices: string[]) => void;
  onText: (questionId: string, text: string) => void;
}) {
  const section = registry.sections.find((s) => s.id === sectionId);
  if (!section) return null;
  const questions = questionsForSection(registry, sectionId);

  return (
    <div>
      <h3 className="font-display font-bold text-h3 text-onyx m-0">{section.title}</h3>
      <p className="text-small text-gray-mid mt-space-1 mb-space-4">{section.purpose}</p>

      <div className="grid gap-space-4">
        <CallFields call={call} sectionId={sectionId} />
        {questions.map((question) =>
          isAskedLive(question, effective, derivedIds) ? (
            <QuestionCard
              key={question.id}
              question={question}
              answer={answers[question.id] ?? BLANK_ANSWER}
              onChoices={onChoices}
              onText={onText}
            />
          ) : null,
        )}
      </div>
    </div>
  );
}

function QuestionCard({
  question,
  answer,
  onChoices,
  onText,
}: {
  question: BuyerInterviewQuestionDto;
  answer: DraftAnswer;
  onChoices: (questionId: string, choices: string[]) => void;
  onText: (questionId: string, text: string) => void;
}) {
  const textId = `q-${question.id}-text`;
  // A partly-derived question offers only the choices the call asks.
  const options = liveOptions(question);

  const toggleMany = (key: string) => {
    const next = answer.choices.includes(key)
      ? answer.choices.filter((k) => k !== key)
      : [...answer.choices, key];
    onChoices(question.id, next);
  };

  return (
    <fieldset
      className="border border-border-light rounded-md p-space-3 m-0"
      data-testid={`interview-question-${question.id}`}
    >
      <legend className="px-space-1 text-small text-onyx">
        {question.label}
        {question.spine && <span className="text-status-error"> *</span>}
      </legend>

      {question.branch && (
        <p className="flex items-start gap-space-1 text-caption text-gray-mid m-0 mb-space-2">
          <CornerDownRight size={12} className="mt-[2px] shrink-0" aria-hidden />
          {question.branch.label}
        </p>
      )}

      {question.verbatim && (
        <p className="text-caption uppercase tracking-wide text-gray-mid m-0 mb-space-2">
          In their words — quote them
        </p>
      )}

      {question.hint && (
        <p className="text-caption text-gray-mid m-0 mb-space-2">{question.hint}</p>
      )}

      {question.kind === "one" && (
        <div className="grid gap-space-1">
          {options.map((option) => (
            <label
              key={option.key}
              className="flex items-start gap-space-2 text-small text-onyx cursor-pointer"
            >
              <input
                type="radio"
                name={`q-${question.id}`}
                value={option.key}
                checked={answer.choices.includes(option.key)}
                onChange={() => onChoices(question.id, [option.key])}
                className="mt-[3px] accent-gold"
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      )}

      {question.kind === "many" && (
        <div className="grid gap-space-1">
          {options.map((option) => (
            <label
              key={option.key}
              className="flex items-start gap-space-2 text-small text-onyx cursor-pointer"
            >
              <input
                type="checkbox"
                value={option.key}
                checked={answer.choices.includes(option.key)}
                onChange={() => toggleMany(option.key)}
                className="mt-[3px] accent-gold"
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      )}

      {question.kind === "free" && (
        <Textarea
          id={textId}
          aria-label={question.label}
          rows={3}
          value={answer.text}
          onChange={(e) => onText(question.id, e.target.value)}
        />
      )}

      {/* A question with choices AND a box: the box sits beside them, never
          instead of them. `free` questions already own the field above. */}
      {question.kind !== "free" && question.textBoxLabel && (
        <div className="mt-space-2">
          <label htmlFor={textId} className="block text-caption text-gray-mid mb-space-1">
            {question.textBoxLabel}
          </label>
          <Input
            id={textId}
            value={answer.text}
            onChange={(e) => onText(question.id, e.target.value)}
          />
        </div>
      )}
    </fieldset>
  );
}

/**
 * The last step: everything recorded, and the gate.
 *
 * <p>The gate states the SHORTFALL as a count — "4 required answers are still
 * missing" — because a disabled button with no explanation is a dead end. It
 * does not say what those answers would have produced, because nobody knows
 * that yet and the server would not say so if they did.</p>
 */
function ReviewStep({
  registry,
  answers,
  recordedCount,
  missingCount,
  spineComplete,
}: {
  registry: BuyerInterviewRegistryDto;
  /** Recorded plus derived — the transcript shows what completion will stamp. */
  answers: DraftAnswers;
  recordedCount: number;
  missingCount: number;
  spineComplete: boolean;
}) {
  return (
    <div>
      <h3 className="font-display font-bold text-h3 text-onyx m-0">Review &amp; finish</h3>
      <p className="text-small text-gray-mid mt-space-1">
        {recordedCount} answers recorded. Completing the interview computes the
        assessment and reveals it — nothing is scored before then.
      </p>

      {!spineComplete && (
        <p
          role="status"
          className="text-small text-status-warning mt-space-2 mb-space-3"
          data-testid="interview-missing"
        >
          {missingCount} required {missingCount === 1 ? "answer is" : "answers are"}{" "}
          still missing. Use the rail to find the sections that are short.
        </p>
      )}

      <div className="mt-space-4">
        <InterviewTranscript registry={registry} answers={answers} />
      </div>
    </div>
  );
}
